import React, { useState, useRef,useEffect} from "react";
import {
    Avatar,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Chip,
    Divider,
    Grid,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { useAuth, useLoading, useSnackbar } from "../../Context/Contexts";
import { useTranslation } from "react-i18next";
import { SearchResultAsync } from "../../Types/ExportTypes";
import type { SvgIconComponent } from "@mui/icons-material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ArchiveIcon from "@mui/icons-material/Archive";
import ArticleIcon from "@mui/icons-material/Article";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import BalanceIcon from "@mui/icons-material/Balance";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import ClearIcon from "@mui/icons-material/Clear";
import CloudIcon from "@mui/icons-material/Cloud";
import CollectionsBookmarkIcon from "@mui/icons-material/CollectionsBookmark";
import DnsIcon from "@mui/icons-material/Dns";
import ExtensionIcon from "@mui/icons-material/Extension";
import FlagIcon from "@mui/icons-material/Flag";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import GroupIcon from "@mui/icons-material/Group";
import HomeIcon from "@mui/icons-material/Home";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import LayersIcon from "@mui/icons-material/Layers";
import LocalLibraryIcon from "@mui/icons-material/LocalLibrary";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PaletteIcon from "@mui/icons-material/Palette";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PlaceIcon from "@mui/icons-material/Place";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import SearchIcon from "@mui/icons-material/Search";
import SettingsIcon from "@mui/icons-material/Settings";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import StorefrontIcon from "@mui/icons-material/Storefront";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import TapAndPlayIcon from "@mui/icons-material/TapAndPlay";
import TabletMacIcon from "@mui/icons-material/TabletMac";
import TranslateIcon from "@mui/icons-material/Translate";
import TuneIcon from "@mui/icons-material/Tune";
import UndoIcon from "@mui/icons-material/Undo";
import VerifiedIcon from "@mui/icons-material/Verified";
import ViewComfyIcon from "@mui/icons-material/ViewComfy";

const iconMap: Record<string, SvgIconComponent> = {
    AccessTime: AccessTimeIcon,
    Archive: ArchiveIcon,
    Article: ArticleIcon,
    Assignment: AssignmentIcon,
    AttachMoney: AttachMoneyIcon,
    Balance: BalanceIcon,
    CardGiftcard: CardGiftcardIcon,
    Cloud: CloudIcon,
    CollectionsBookmark: CollectionsBookmarkIcon,
    Dns: DnsIcon,
    Extension: ExtensionIcon,
    Flag: FlagIcon,
    FormatListBulleted: FormatListBulletedIcon,
    Group: GroupIcon,
    Home: HomeIcon,
    InsertDriveFile: InsertDriveFileIcon,
    Layers: LayersIcon,
    LocalLibrary: LocalLibraryIcon,
    LocalShipping: LocalShippingIcon,
    LocationCity: LocationCityIcon,
    Palette: PaletteIcon,
    PictureAsPdf: PictureAsPdfIcon,
    Place: PlaceIcon,
    ReceiptLong: ReceiptLongIcon,
    RocketLaunch: RocketLaunchIcon,
    Settings: SettingsIcon,
    ShoppingCart: ShoppingCartIcon,
    ShowChart: ShowChartIcon,
    Storefront: StorefrontIcon,
    SwapVert: SwapVertIcon,
    TapAndPlay: TapAndPlayIcon,
    TabletMac: TabletMacIcon,
    Translate: TranslateIcon,
    Tune: TuneIcon,
    Undo: UndoIcon,
    Verified: VerifiedIcon,
    ViewComfy: ViewComfyIcon,
};

const SearchComponent: React.FC = () => {
    const { authService } = useAuth();
    const { showLoading, hideLoading, isLoading } = useLoading();
    const { showSnackbar } = useSnackbar();
    const { t } = useTranslation();

    const [term, setTerm] = useState("");
    const [activeTerm, setActiveTerm] = useState("");
    const [results, setResults] = useState<SearchResultAsync[]>([]);
    const [hasSearched, setHasSearched] = useState(false);
    const [lastFetchCount, setLastFetchCount] = useState(0);

    const searchRef = useRef<HTMLInputElement>(null);


    useEffect(() => {
        searchRef.current?.focus();
    }, []);

    const fetchResults = async (searchTerm: string, options?: { append?: boolean; skip?: number }) => {
        if (!searchTerm) {
            setResults([]);
            setLastFetchCount(0);
            return;
        }

        const { append = false, skip } = options ?? {};

        showLoading();
        try {
            const params = new URLSearchParams({ term: searchTerm });
            if (skip && skip > 0) {
                params.append("skip", skip.toString());
            }

            const response = await authService.get<SearchResultAsync[] | null>(`/searchApi/Search?${params.toString()}`);
            const newResults = response ?? [];

            setResults((previous) => (append ? [...previous, ...newResults] : newResults));
            setLastFetchCount(newResults.length);

        } catch (error: any) {
            setLastFetchCount(0);
            showSnackbar(`${t("common.failedToFetchData")}: ${error.response?.data ?? error.message}`, "error");
        } finally {
            hideLoading();
            searchRef.current?.focus();
        }
    };

    const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const trimmedTerm = term.trim();

        if (!trimmedTerm) {
            setActiveTerm("");
            setHasSearched(false);
            setResults([]);
            setLastFetchCount(0);
            return;
        }

        setActiveTerm(trimmedTerm);
        setHasSearched(true);
        fetchResults(trimmedTerm);
    };

    const handleFetchMore = () => {
        if (!activeTerm) {
            return;
        }

        fetchResults(activeTerm, { append: true, skip: results.length });
    };

    const handleClear = () => {
        setTerm("");
        setActiveTerm("");
        setHasSearched(false);
        setResults([]);
        setLastFetchCount(0);
    };

    const renderIcon = (icon?: string | null) => {
        if (!icon) {
            return <SearchIcon fontSize="small" />;
        }

        const IconComponent = iconMap[icon];

        if (!IconComponent) {
            return <SearchIcon fontSize="small" />;
        }

        return <IconComponent fontSize="small" />;
    };

    return (
        <Box sx={{ width: "100%" }}>
            <Box sx={{

                backgroundColor: "background.default",
                        padding: 3,
            }}>
               
                    <Typography variant="h4" sx={{ mb: 2, textAlign: "center" }}>
                        {t("common.search")}
                    </Typography>

                    <Box component="form" onSubmit={handleSearchSubmit} sx={{ mb: 3 }}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={9}>
                                <TextField
                                    label={t("common.search")}
                                variant="outlined"
                                inputRef={searchRef}
                                    fullWidth
                                    value={term}
                                    onChange={(event) => setTerm(event.target.value)}
                                    size="small"
                                    disabled={isLoading}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" />
                                            </InputAdornment>
                                        ),
                                        endAdornment: term ? (
                                            <InputAdornment position="end">
                                                <Tooltip title={t("common.clear") ?? ""}>
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            onClick={handleClear}
                                                            disabled={isLoading}
                                                            aria-label={t("common.clear") ?? "clear"}
                                                        >
                                                            <ClearIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </InputAdornment>
                                        ) : undefined,
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={3} sx={{ display: "flex", alignItems: "stretch" }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    type="submit"
                                    fullWidth
                                    disabled={isLoading}
                                    startIcon={<SearchIcon />}
                                >
                                    {t("common.search")}
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>

                    {hasSearched && activeTerm && (
                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ mb: 2 }}
                            useFlexGap
                            flexWrap="wrap"
                        >
                            <Typography variant="body2" color="text.secondary">
                                {t("common.search")}:
                            </Typography>
                            <Chip label={activeTerm} size="small" color="primary" variant="outlined" />
                            {results.length > 0 && (
                                <Chip
                                    label={`${results.length} ${t("common.results") ?? "results"}`}
                                    size="small"
                                    variant="outlined"
                                />
                            )}
                        </Stack>
                    )}

                    <Grid container spacing={2}>
                        {results.map((result, index) => (
                            <Grid item xs={12} key={`${result.identifier ?? "result"}-${index}`}>
                                <Card
                                    variant="outlined"
                                    sx={{
                                        borderRadius: 2,
                                        borderColor: "divider",
                                        transition: "box-shadow 0.2s ease, border-color 0.2s ease",
                                        height: "100%",
                                        display: "flex",
                                        flexDirection: "column",
                                        "&:hover": {
                                            boxShadow: 6,
                                            borderColor: "primary.light",
                                        },
                                    }}
                                >
                                    <CardHeader
                                        sx={{ pb: 0 }}
                                        avatar={
                                            <Avatar
                                                variant="rounded"
                                                sx={{
                                                    bgcolor: "primary.main",
                                                    color: "primary.contrastText",
                                                    width: 40,
                                                    height: 40,
                                                }}
                                            >
                                                {renderIcon(result.icon)}
                                            </Avatar>
                                        }
                                        titleTypographyProps={{ variant: "subtitle1", fontWeight: 600 }}
                                        title={result.description ?? result.identifier ?? ""}
                                    />
                                    <CardContent sx={{ pt: 1, flexGrow: 1 }}>
                                        <Stack spacing={1}>
                                            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                                {result.identifier && (
                                                    <Chip label={result.identifier} size="small" color="primary" variant="outlined" />
                                                )}
                                                {result.keyWord1 && (
                                                    <Chip
                                                        label={`${t("common.keyword")}: ${result.keyWord1}`}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                )}

                                            </Stack>

                                        </Stack>
                                    </CardContent>
                                    {result.url && (
                                        <>
                                            <Divider sx={{ mt: 0 }} />
                                            <CardActions sx={{ justifyContent: "flex-end", px: 2, pb: 2 }}>
                                                <Button
                                                    component="a"
                                                    href={result.url ?? undefined}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    variant="contained"
                                                    endIcon={<OpenInNewIcon fontSize="small" />}
                                                >
                                                    {t("common.open")}
                                                </Button>
                                            </CardActions>
                                        </>
                                    )}
                                </Card>
                            </Grid>
                        ))}

                        {!isLoading && hasSearched && results.length === 0 && (
                            <Grid item xs={12}>
                                <Typography align="center">{t("common.noEntriesFound")}</Typography>
                            </Grid>
                        )}
                    </Grid>

                    {results.length > 0 && lastFetchCount > 0 && (
                        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                            <Button variant="outlined" onClick={handleFetchMore} disabled={isLoading}>
                                {t("common.fetchMore")}
                            </Button>
                        </Box>
                    )}
         
            </Box>
        </Box>


    );
};

export default SearchComponent;
